import { ReactNode } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FieldLabel } from '@cash-design-system/react'
import { EmailBlocker } from '@cash-web/protos/squareup/franklin/blockers/email_blockers.pb'
import { EmailBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationRegisterEmail } from '@cash-web/shared-data-access-api/react-query/useMutationRegisterEmail'
import { FormInput } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { isValidEmail } from '@cash-web/shared-util-validation'

import { getRegisterEmailErrors } from '../../util/statusErrors'
import { BaseBlocker, SubmitButton } from '../BaseBlocker/BaseBlocker'

export type BlockerEmailProps = {
  blocker?: EmailBlocker
  onSubmit?: (data: EmailForm) => void
  footer?: ReactNode
  buttons?: (((email: string) => ReactNode) | ReactNode)[]
  defaultEmail?: string
  supplement?: EmailBlockerSupplement
}
export type EmailForm = {
  email: string
}
export const BlockerEmail = ({ onSubmit, footer, buttons, supplement, defaultEmail = '' }: BlockerEmailProps) => {
  const { t } = useTranslation()
  const form = useForm<EmailForm>({
    mode: 'onTouched',
    defaultValues: {
      email: defaultEmail,
    },
  })
  const email = form.watch('email')
  const flow = useFlowContext()
  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()
  const { mutateAsync: registerEmail } = useMutationRegisterEmail({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })

  const handleSubmit = async (data: EmailForm) => {
    const { email } = data
    await handleBlockerResponse({
      promisedBlockerUpdate: registerEmail({
        email_address: email,
        existing_customer_only: false,
        payment_tokens: flow.active?.paymentTokens,
      }),
      errors: getRegisterEmailErrors(genericError, t),
    })
    onSubmit?.(data)
  }

  const errorMessage = t('Enter a valid email address.', { context: 'Input Error.' })
  return (
    <BaseBlocker
      mainText={supplement?.main_text ?? t('Enter your email', { context: 'Title text.' })}
      secondaryText={
        supplement?.headline ??
        t("We'll send a confirmation code to this address to verify that it's yours.", {
          context: 'Subtitle text. The code is a series of digits and hyphens.',
        })
      }
      footer={footer}
      onSubmit={handleSubmit}
      form={form}
      buttons={
        buttons
          ? buttons.map(button => (typeof button === 'function' ? button(email) : button))
          : [<SubmitButton key="submit-email-btn" />]
      }
    >
      <FieldLabel label={t('Email', { context: 'Input Label.' })}>
        <FormInput
          placeholder={t('Your email address', { context: 'Input Placeholder.' })}
          name="email"
          autoFocus
          inputMode="email"
          autoCapitalize="off"
          autoComplete="email"
          spellCheck="false"
          autoCorrect="off"
          rules={{
            required: {
              message: errorMessage,
              value: true,
            },
            validate: (value: string) => {
              return isValidEmail(value) || errorMessage
            },
          }}
        />
      </FieldLabel>
    </BaseBlocker>
  )
}
